/* ═══════════════════════════════════════════════════════════════════════════
   Cube 3D — Filament Quiz
   Web component: <filament-quiz>
   Reads filament data from #gs-filament-data, runs a 4-step quiz,
   classifies products into material categories, scores & shows ranked
   results with specific product picks per category.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Quiz questions ───────────────────────────────────────────────────── */
  var QUESTIONS = [
    {
      id: 'budget',
      title: 'What\u2019s your filament budget?',
      hint: 'Price per 1 kg spool',
      multi: false,
      cols: 1,
      options: [
        { value: '16-20', label: '$16 \u2013 $20', desc: 'Everyday PLA, budget-friendly' },
        { value: '21-30', label: '$21 \u2013 $30', desc: 'Wider choice and special finishes' },
        { value: '30+',   label: '$30+',            desc: 'Premium & engineering materials' }
      ]
    },
    {
      id: 'use',
      title: 'What will you print?',
      hint: 'Pick one or more',
      multi: true,
      cols: 2,
      options: [
        { value: 'toys',        label: 'Toys & Figurines',     desc: 'Models, miniatures, display' },
        { value: 'functional',  label: 'Functional Parts',     desc: 'Tools, brackets, replacements' },
        { value: 'prototyping', label: 'Prototyping',          desc: 'Quick iterations, test fits' },
        { value: 'art',         label: 'Art & Decoration',     desc: 'Vases, sculptures, home d\u00e9cor' },
        { value: 'education',   label: 'Education / Learning', desc: 'School, STEM, kids' }
      ]
    },
    {
      id: 'properties',
      title: 'What properties matter most?',
      hint: 'Pick one or more',
      multi: true,
      cols: 2,
      options: [
        { value: 'strength',    label: 'Strength',        desc: 'Durable, impact-resistant' },
        { value: 'flexibility', label: 'Flexibility',     desc: 'Bends without breaking' },
        { value: 'heat',        label: 'Heat Resistance', desc: 'Survives heat, sun, outdoors' },
        { value: 'appearance',  label: 'Appearance',      desc: 'Silk, matte, smooth surface' },
        { value: 'easy',        label: 'Easy to Print',   desc: 'Low warping, works on any printer' }
      ]
    },
    {
      id: 'environment',
      title: 'Where will the printed part be used?',
      hint: 'Pick one',
      multi: false,
      cols: 2,
      options: [
        { value: 'indoor',     label: 'Indoor Display',    desc: 'Shelf, desk, room decoration' },
        { value: 'outdoor',    label: 'Outdoor Use',       desc: 'Garden, car, sun exposure' },
        { value: 'mechanical', label: 'Mechanical Stress', desc: 'Gears, hinges, load-bearing' },
        { value: 'food',       label: 'Food Contact',      desc: 'Cookie cutters, short contact only' }
      ]
    }
  ];

  /* ── Material category definitions ────────────────────────────────────── */
  /* Each category has scoring weights for budget, use case, properties,
     and environment. Max per dimension: budget 10, use 15 (capped),
     properties 15 (capped), environment 10. Total theoretical max = 50. */
  var CATEGORIES = {
    'pla': {
      name: 'PLA / PLA+',
      desc: 'The go-to beginner filament - easy to print, low warping, no enclosure needed.',
      warning: null,
      budget:  { '16-20': 10, '21-30': 6, '30+': 0 },
      use:     { toys: 6, functional: 4, prototyping: 10, art: 0, education: 10 },
      props:   { strength: 3, flexibility: 0, heat: 0, appearance: 0, easy: 10 },
      env:     { indoor: 8, outdoor: 0, mechanical: 2, food: 6 }
    },
    'pla-silk': {
      name: 'Silk PLA',
      desc: 'Shiny, metallic-looking surface - perfect for display pieces and gifts.',
      warning: null,
      budget:  { '16-20': 6, '21-30': 10, '30+': 2 },
      use:     { toys: 8, functional: 0, prototyping: 0, art: 10, education: 2 },
      props:   { strength: 0, flexibility: 0, heat: 0, appearance: 10, easy: 6 },
      env:     { indoor: 10, outdoor: 0, mechanical: 0, food: 0 }
    },
    'pla-exotic': {
      name: 'Specialty PLA',
      desc: 'Unique finishes - marble, wood, metal, glow-in-the-dark, matte and more.',
      warning: null,
      budget:  { '16-20': 4, '21-30': 10, '30+': 4 },
      use:     { toys: 6, functional: 0, prototyping: 0, art: 10, education: 4 },
      props:   { strength: 0, flexibility: 0, heat: 0, appearance: 10, easy: 5 },
      env:     { indoor: 10, outdoor: 0, mechanical: 0, food: 0 }
    },
    'petg': {
      name: 'PETG',
      desc: 'Stronger than PLA with good chemical and heat resistance. Great all-rounder.',
      warning: null,
      budget:  { '16-20': 8, '21-30': 8, '30+': 2 },
      use:     { toys: 4, functional: 10, prototyping: 8, art: 0, education: 4 },
      props:   { strength: 7, flexibility: 3, heat: 5, appearance: 0, easy: 5 },
      env:     { indoor: 6, outdoor: 6, mechanical: 8, food: 10 }
    },
    'abs': {
      name: 'ABS',
      desc: 'Heat-resistant and impact-tough. Industry standard for durable parts.',
      warning: '\u26a0\ufe0f Needs enclosed printer + ventilation',
      budget:  { '16-20': 6, '21-30': 10, '30+': 2 },
      use:     { toys: 0, functional: 10, prototyping: 6, art: 0, education: 0 },
      props:   { strength: 8, flexibility: 0, heat: 10, appearance: 0, easy: 0 },
      env:     { indoor: 4, outdoor: 4, mechanical: 10, food: 0 }
    },
    'asa': {
      name: 'ASA',
      desc: 'UV and weather resistant - the best filament for outdoor parts.',
      warning: '\u26a0\ufe0f Best with enclosed printer',
      budget:  { '16-20': 4, '21-30': 10, '30+': 4 },
      use:     { toys: 0, functional: 10, prototyping: 4, art: 0, education: 0 },
      props:   { strength: 7, flexibility: 0, heat: 10, appearance: 0, easy: 0 },
      env:     { indoor: 2, outdoor: 10, mechanical: 6, food: 0 }
    },
    'flexible': {
      name: 'TPU / Flexible',
      desc: 'Rubber-like material that bends without breaking - phone cases, gaskets, grips.',
      warning: null,
      budget:  { '16-20': 4, '21-30': 8, '30+': 6 },
      use:     { toys: 6, functional: 8, prototyping: 4, art: 4, education: 4 },
      props:   { strength: 4, flexibility: 10, heat: 0, appearance: 0, easy: 2 },
      env:     { indoor: 6, outdoor: 4, mechanical: 6, food: 0 }
    },
    'nylon': {
      name: 'Nylon (PA)',
      desc: 'Extremely strong and wear-resistant - gears, bearings, structural parts.',
      warning: '\u26a0\ufe0f Needs enclosed printer + must be kept dry',
      budget:  { '16-20': 0, '21-30': 4, '30+': 10 },
      use:     { toys: 0, functional: 10, prototyping: 4, art: 0, education: 0 },
      props:   { strength: 10, flexibility: 4, heat: 7, appearance: 0, easy: 0 },
      env:     { indoor: 4, outdoor: 4, mechanical: 10, food: 0 }
    },
    'cf': {
      name: 'Carbon & Glass Fiber Composites',
      desc: 'Lightweight and extremely rigid - for drone frames, RC parts, engineering.',
      warning: '\u26a0\ufe0f Needs hardened nozzle + enclosed printer',
      budget:  { '16-20': 0, '21-30': 0, '30+': 10 },
      use:     { toys: 0, functional: 10, prototyping: 2, art: 0, education: 0 },
      props:   { strength: 10, flexibility: 0, heat: 8, appearance: 0, easy: 0 },
      env:     { indoor: 4, outdoor: 4, mechanical: 10, food: 0 }
    },
    'pc': {
      name: 'Polycarbonate (PC)',
      desc: 'Outstanding impact resistance and heat tolerance - tough engineering material.',
      warning: '\u26a0\ufe0f Needs enclosed printer + high-temp hotend',
      budget:  { '16-20': 0, '21-30': 4, '30+': 10 },
      use:     { toys: 0, functional: 10, prototyping: 2, art: 0, education: 0 },
      props:   { strength: 10, flexibility: 0, heat: 10, appearance: 0, easy: 0 },
      env:     { indoor: 4, outdoor: 4, mechanical: 10, food: 0 }
    }
  };

  /* ── Classify a product into a material category from its title ──────── */
  function classifyProduct(title) {
    var t = title.toLowerCase();
    if (t.indexOf('pen') !== -1 && t.indexOf('pcl') !== -1) return 'exclude';
    if (/-cf\b/.test(t) || t.indexOf('carbon fiber') !== -1) return 'cf';
    if (/-gf\b/.test(t) || t.indexOf('glass fiber') !== -1) return 'cf';
    if (/\bpps\b/.test(t)) return 'cf';
    if (/\bppa\b/.test(t)) return 'nylon';
    if (/\bpa\s/.test(t) || /\bpa\d/.test(t) || /\bpaht\b/.test(t) || /\bnylon\b/.test(t)) return 'nylon';
    if (/\bpc\b/.test(t)) return 'pc';
    if (/\babs/.test(t)) return 'abs';
    if (/\basa\b/.test(t)) return 'asa';
    if (/\btpu\b/.test(t) || /\btpe\b/.test(t) || t.indexOf('flexible') !== -1) return 'flexible';
    if (/\bpetg\b/.test(t)) return 'petg';
    if (/silk/.test(t) && /pla/.test(t)) return 'pla-silk';
    if (/pla/.test(t) && /marble|wood|metal|galaxy|sparkle|glow|translucent|chameleon|twinkling|uv.color|rock|candy|mystic|rainbow|magic|matte/.test(t)) return 'pla-exotic';
    if (/\bpla\b/.test(t)) return 'pla';
    return 'other';
  }

  /* ── HTML escape ──────────────────────────────────────────────────────── */
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Parse price cents ────────────────────────────────────────────────── */
  function parseCents(str) {
    var n = parseInt(str, 10);
    return isNaN(n) ? 0 : n;
  }

  /* ── Score a category against user answers ────────────────────────────── */
  function scoreCategory(cat, answers) {
    var score = 0;

    // Budget (single-select, max 10)
    var budget = (answers.budget || [])[0] || '16-20';
    score += (cat.budget[budget] || 0);

    // Use case (multi-select, sum, cap at 15)
    var useCases = answers.use || [];
    var ucTotal = 0;
    for (var i = 0; i < useCases.length; i++) {
      ucTotal += (cat.use[useCases[i]] || 0);
    }
    score += Math.min(ucTotal, 15);

    // Properties (multi-select, sum, cap at 15)
    var props = answers.properties || [];
    var prTotal = 0;
    for (var j = 0; j < props.length; j++) {
      prTotal += (cat.props[props[j]] || 0);
    }
    score += Math.min(prTotal, 15);

    // Environment (single-select, max 10)
    var env = (answers.environment || [])[0] || 'indoor';
    score += (cat.env[env] || 0);

    return score;
  }

  /* ── Web Component ────────────────────────────────────────────────────── */
  class FilamentQuiz extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.currentStep = 0;
      this.answers = {};
      this.waNumber = this.dataset.whatsappNumber || '';
      this.waMessage = this.dataset.whatsappMessage || '';
    }

    connectedCallback() {
      this.loadProducts();
      this.bodyEl = this.querySelector('#gs-fq-body');
      this.resultsEl = this.querySelector('#gs-fq-results');
      this.renderQuestion();
    }

    loadProducts() {
      var arr = [];
      try {
        var el = document.getElementById('gs-filament-json');
        if (el) {
          var raw = JSON.parse(el.textContent);
          for (var i = 0; i < raw.length; i++) {
            var p = raw[i];
            if (!p.available) continue;
            p._category = classifyProduct(p.title || '');
            if (p._category !== 'exclude' && p._category !== 'other') {
              arr.push(p);
            }
          }
        }
      } catch (e) { /* silent */ }
      this.products = arr;
    }

    /* ── Render question ──────────────────────────────────────────────── */
    renderQuestion() {
      var q = QUESTIONS[this.currentStep];
      var self = this;
      var selected = this.answers[q.id] || [];
      var totalSteps = QUESTIONS.length;
      var stepNum = this.currentStep + 1;
      var pct = Math.round((stepNum / totalSteps) * 100);

      var html = '';

      // Progress bar
      html += '<div class="gs-quiz__progress">';
      html += '<p class="gs-quiz__progress-text">Step ' + stepNum + ' of ' + totalSteps + '</p>';
      html += '<div class="gs-quiz__progress-bar"><div class="gs-quiz__progress-fill" style="width:' + pct + '%"></div></div>';
      html += '</div>';

      // Question
      html += '<div class="gs-quiz__question">';
      html += '<h3 class="gs-quiz__question-title">' + esc(q.title) + '</h3>';
      if (q.hint) html += '<p class="gs-quiz__question-hint">' + esc(q.hint) + '</p>';
      html += '</div>';

      // Options
      var colsClass = q.cols === 2 ? ' gs-quiz__options--cols-2' : '';
      html += '<div class="gs-quiz__options' + colsClass + '">';
      q.options.forEach(function (opt) {
        var isSel = selected.indexOf(opt.value) !== -1;
        html += '<div class="gs-quiz__option' + (isSel ? ' is-selected' : '') + '" data-value="' + esc(opt.value) + '" role="button" tabindex="0" aria-pressed="' + isSel + '">';
        html += '<div class="gs-quiz__option-check">';
        html += '<svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        html += '</div>';
        html += '<div>';
        html += '<div class="gs-quiz__option-label">' + esc(opt.label) + '</div>';
        if (opt.desc) html += '<div class="gs-quiz__option-desc">' + esc(opt.desc) + '</div>';
        html += '</div>';
        html += '</div>';
      });
      html += '</div>';

      // Navigation
      html += '<div class="gs-quiz__nav">';
      if (this.currentStep > 0) {
        html += '<button class="gs-quiz__btn gs-quiz__btn--back" type="button" id="gs-fq-prev">';
        html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>';
        html += ' Back</button>';
      }
      var isLast = this.currentStep === totalSteps - 1;
      var nextLabel = isLast ? 'See Results' : 'Next';
      html += '<button class="gs-quiz__btn gs-quiz__btn--next" type="button" id="gs-fq-next"' + (selected.length === 0 ? ' disabled' : '') + '>';
      html += nextLabel + ' ';
      html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>';
      html += '</button>';
      html += '</div>';

      this.bodyEl.innerHTML = html;
      this.resultsEl.classList.remove('is-visible');
      this.resultsEl.innerHTML = '';

      // Bind option clicks
      this.bodyEl.querySelectorAll('.gs-quiz__option').forEach(function (el) {
        var handler = function () {
          var val = el.dataset.value;
          var q2 = QUESTIONS[self.currentStep];
          var arr = self.answers[q2.id] || [];

          if (q2.multi) {
            var idx = arr.indexOf(val);
            if (idx === -1) arr.push(val);
            else arr.splice(idx, 1);
          } else {
            arr = [val];
          }
          self.answers[q2.id] = arr;
          self.renderQuestion();
        };

        el.addEventListener('click', handler);
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(); }
        });
      });

      // Bind nav
      var prevBtn = this.bodyEl.querySelector('#gs-fq-prev');
      var nextBtn = this.bodyEl.querySelector('#gs-fq-next');

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          self.currentStep--;
          self.renderQuestion();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if ((self.answers[QUESTIONS[self.currentStep].id] || []).length === 0) return;
          if (self.currentStep < totalSteps - 1) {
            self.currentStep++;
            self.renderQuestion();
          } else {
            self.showResults();
          }
        });
      }
    }

    /* ── Calculate & show results ─────────────────────────────────────── */
    showResults() {
      var answers = this.answers;
      var products = this.products;

      // Score each category and find matching products
      var catIds = Object.keys(CATEGORIES);
      var scored = [];

      for (var i = 0; i < catIds.length; i++) {
        var catId = catIds[i];
        var cat = CATEGORIES[catId];
        var catScore = scoreCategory(cat, answers);

        // Never suggest these for food contact; TPU only when Flexibility was asked for
        var env = (answers.environment || [])[0];
        if (env === 'food' && catId !== 'pla' && catId !== 'petg') continue;
        if (catId === 'flexible' && (answers.properties || []).indexOf('flexibility') === -1) continue;

        // Products in this category (bulk "Big Spool" products are for repeat buyers, not first picks)
        var catProducts = [];
        for (var j = 0; j < products.length; j++) {
          if (products[j]._category === catId && !/big spool/i.test(products[j].title)) {
            catProducts.push(products[j]);
          }
        }
        // Prefer products inside the chosen budget, keep the rest as a fallback
        var tier = (answers.budget || [])[0] || '16-20';
        var lo = tier === '30+' ? 3000 : tier === '21-30' ? 2100 : 0;
        var hi = tier === '30+' ? Infinity : tier === '21-30' ? 3099 : 2099;
        var inTier = catProducts.filter(function (p) { var c = parseCents(p.priceCents); return c >= lo && c <= hi; });
        if (inTier.length) catProducts = inTier;
        this.tierMax = hi;

        // Skip categories with no products in store
        if (catProducts.length === 0) continue;

        // Sort by price ascending
        catProducts.sort(function (a, b) {
          return parseCents(a.priceCents) - parseCents(b.priceCents);
        });

        // Pick up to 2 products: cheapest + cheapest from a different vendor
        var picks = [catProducts[0]];
        for (var k = 1; k < catProducts.length; k++) {
          if (catProducts[k].vendor !== picks[0].vendor) {
            picks.push(catProducts[k]);
            break;
          }
        }
        if (picks.length === 1 && catProducts.length > 1) {
          picks.push(catProducts[1]);
        }

        scored.push({
          id: catId,
          name: cat.name,
          desc: cat.desc,
          warning: cat.warning,
          score: catScore,
          picks: picks
        });
      }

      // Sort by score descending
      scored.sort(function (a, b) { return b.score - a.score; });
      this.foodNote = (answers.environment || [])[0] === 'food';

      // Show up to 5, but only materials that score at least half of the best match
      // (a parent buying PLA for a child shouldn't see ABS as a 5th pick)
      var cutoff = scored.length ? scored[0].score * 0.5 : 0;
      var top = scored.filter(function (c) { return c.score >= cutoff; }).slice(0, 5);
      var maxScore = 50;

      this.renderResults(top, maxScore);
    }

    /* ── Render results ───────────────────────────────────────────────── */
    renderResults(topCategories, maxScore) {
      this.bodyEl.innerHTML = '';
      var resultsEl = this.resultsEl;
      resultsEl.classList.add('is-visible');

      var html = '';
      html += '<h2 class="gs-results__heading">Your Filament Matches</h2>';
      html += '<p class="gs-results__sub">Ranked by how well each material fits your answers.</p>';
      if (this.foodNote) {
        html += '<p class="gs-results__note gs-results__note--warn"><strong>Food safety:</strong> 3D-printed parts are not food-safe by default. '
          + 'Fine for a cookie cutter used briefly; not for cups, plates or utensils you eat or drink from.</p>';
      }

      if (topCategories.length === 0) {
        html += '<div class="gs-results__empty">';
        html += '<p class="gs-results__empty-text">We couldn\u2019t find a match. Try different answers or chat with our team.</p>';
        html += '</div>';
      } else {
        html += '<div class="gs-fq__list">';
        var rankLabels = ['Best Match', '2nd Pick', '3rd Pick', '4th Pick', '5th Pick'];

        for (var i = 0; i < topCategories.length; i++) {
          var cat = topCategories[i];

          html += '<div class="gs-fq__card' + (i === 0 ? ' gs-fq__card--top' : '') + '">';

          // Header
          html += '<div class="gs-fq__card-header">';
          html += '<span class="gs-fq__rank">' + esc(rankLabels[i]) + '</span>';
          html += '</div>';

          // Body
          html += '<div class="gs-fq__card-body">';
          html += '<h3 class="gs-fq__cat-name">' + esc(cat.name) + '</h3>';
          html += '<p class="gs-fq__cat-desc">' + esc(cat.desc) + '</p>';

          if (cat.warning) {
            html += '<div class="gs-fq__warning">' + cat.warning + '</div>';
          }

          // Product picks
          html += '<div class="gs-fq__picks">';
          html += '<p class="gs-fq__picks-label">Top Picks</p>';

          for (var j = 0; j < cat.picks.length; j++) {
            var p = cat.picks[j];
            html += '<div class="gs-fq__pick">';
            html += '<div class="gs-fq__pick-img">';
            if (p.image) {
              html += '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="80" height="80">';
            }
            html += '</div>';
            html += '<div class="gs-fq__pick-info">';
            html += '<div class="gs-fq__pick-vendor">' + esc(p.vendor) + '</div>';
            html += '<div class="gs-fq__pick-name">' + esc(p.title.replace(' Filament', '')) + '</div>';
            html += '<div class="gs-fq__pick-price">' + esc(p.price) + '</div>';
            if (parseCents(p.priceCents) > this.tierMax) {
              html += '<div class="gs-fq__pick-over">Just over your budget</div>';
            }
            if (p.coloursTotal > 1) {
              html += '<div class="gs-fq__pick-stock">' + p.coloursInStock + ' of ' + p.coloursTotal + ' colors in stock</div>';
            }
            html += '<a class="gs-fq__pick-link" href="' + esc(p.url) + '">' + (p.coloursTotal > 1 ? 'Choose color' : 'View Product') + ' \u2192</a>';
            html += '</div>';
            html += '</div>';
          }

          html += '</div>'; // picks
          html += '</div>'; // body
          html += '</div>'; // card
        }
        html += '</div>'; // list
      }

      // WhatsApp CTA
      var waNum = this.waNumber;
      var waMsg = this.waMessage;
      if (waNum) {
        html += '<div class="gs-whatsapp">';
        html += '<h3 class="gs-whatsapp__heading">Need help choosing?</h3>';
        html += '<p class="gs-whatsapp__text">Our team can recommend the perfect filament for your specific project.</p>';
        html += '<a class="gs-whatsapp__btn" href="https://wa.me/' + esc(waNum) + '?text=' + esc(waMsg) + '" target="_blank" rel="noopener">';
        html += '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.75.75 0 00.917.918l4.462-1.493A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.29 0-4.408-.763-6.104-2.05a.75.75 0 00-.63-.132l-3.065 1.025 1.027-3.072a.75.75 0 00-.131-.63A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>';
        html += ' Chat on WhatsApp</a>';
        html += '</div>';
      }

      // Restart
      html += '<div class="gs-quiz__restart">';
      html += '<button class="gs-quiz__restart-btn" type="button" id="gs-fq-restart">';
      html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>';
      html += ' Retake Quiz</button>';
      html += '</div>';

      resultsEl.innerHTML = html;

      // Bind restart
      var self = this;
      var restartBtn = resultsEl.querySelector('#gs-fq-restart');
      if (restartBtn) {
        restartBtn.addEventListener('click', function () {
          self.currentStep = 0;
          self.answers = {};
          self.renderQuestion();
          document.getElementById('gs-filament-quiz-section').scrollIntoView({ behavior: 'smooth' });
        });
      }
    }
  }

  customElements.define('filament-quiz', FilamentQuiz);

}());
